import { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { deliveryAddressInformationSchema } from 'utils/form-validation-schema';
import { setAddressDetails } from 'services/UserAccountService';
import { DeleteAddressPrompt } from './Prompts/DeleteAddressPrompt';
import { MakePrimaryPrompt } from './Prompts/MakePrimaryPrompt';
import { AddressInformation } from './AddressInformation';
import {
  mapAddressToFields,
  mapFieldsToAPI,
  setValuesOnAddresses,
  completeFormErrorMessage,
} from './delivery-address-form-utils';

import './DeliveryAddressForm.scss';

export const DeliveryAddressForm = (props) => {
  const { currentAddressModel } = props;

  // State managment
  const [isBusy, setIsBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [removeAddress, setRemoveAddress] = useState();
  const [primaryAddress, setPrimaryAddress] = useState();

  // Select Primary Address from data if present
  let defaultPrimaryAddress = {};
  if (currentAddressModel.PrimaryAddress) {
    defaultPrimaryAddress = mapAddressToFields(currentAddressModel.PrimaryAddress);
  }

  // Select all Secondary Addressess from data if present
  let defaultSecondaryAddresses = [];
  if (currentAddressModel.SecondaryAddresses && currentAddressModel.SecondaryAddresses.length) {
    defaultSecondaryAddresses = currentAddressModel.SecondaryAddresses.map((a) =>
      mapAddressToFields(a)
    );
  }

  const methods = useForm({
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: yupResolver(deliveryAddressInformationSchema),
    defaultValues: {
      ...defaultPrimaryAddress,
      secondary: defaultSecondaryAddresses,
    },
  });

  const { formState, control, getValues, setValue, trigger } = methods;
  const { errors, isDirty, isValid } = formState;
  const formHasErrors = Object.keys(errors).length > 0;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'secondary', // unique name for your Field Array
  });

  /**
   * FORM SUBMIT
   */
  const handleDeliveryFormSubmit = async (data) => {
    try {
      setIsBusy(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      const payload = mapFieldsToAPI(data);
      const response = await setAddressDetails(payload);

      setValuesOnAddresses(response, setValue);
      setSuccessMessage(response.SuccessMessage);
      setErrorMessage(response.ErrorMessage);
      setIsBusy(false);
    } catch {
      setErrorMessage('Please complete change address detail form.');
    }
    setIsBusy(false);
  };

  const handleAddAddress = () => {
    // Append new empty node with default country and state to Aus Victoria
    const defaultCountry = currentAddressModel.Countries.find(
      (country) => country.Description === 'Australia'
    );
    const defaultState = currentAddressModel.AustralianStates.find(
      (state) => state.Description === 'Victoria'
    );

    append(
      {
        state: defaultState.StateCode,
        stateDescription: defaultState.Description,
        stateId: defaultState.Id,
        country: defaultCountry.Description,
        countryId: defaultCountry.Id,
      },
      {
        // faking this to prevent auto focusing on first field of appended form
        focusName: 'null',
      }
    );
  };

  const closeModalHandler = () => {
    setRemoveAddress(null);
    setPrimaryAddress(null);
  };

  const handleRemoveAddress = (event, index) => {
    const address = getValues().secondary[index];
    address.index = index; // Save index for reference so we can remove from array after confirm
    setRemoveAddress(address);
    setErrorMessage(false);
    event.preventDefault();
  };

  /**
   * Clean up method that runs after remove was confirmed
   */
  const addressPromptRemoveHandler = (address, successString, errorString) => {
    remove(address.index);
    setSuccessMessage(successString);
    setErrorMessage(errorString);
  };

  const handleMakePrimary = (event, index) => {
    if (isValid) {
      const address = getValues().secondary[index];
      address.index = index; // Save index for reference so we can update specific item in array
      setPrimaryAddress(address, index);
      setErrorMessage(false);
    } else {
      setErrorMessage(completeFormErrorMessage);
      trigger();
    }

    event.preventDefault();
  };

  return (
    <>
      <DeleteAddressPrompt
        active={!!removeAddress}
        onRequestClose={closeModalHandler}
        onRemoveAddress={addressPromptRemoveHandler}
        address={removeAddress}
        getValues={getValues}
      />
      <MakePrimaryPrompt
        active={!!primaryAddress}
        onRequestClose={closeModalHandler}
        address={primaryAddress}
        getValues={getValues}
      />
      <FormProvider {...methods}>
        <form
          name="deliveryAddressForm"
          noValidate
          onSubmit={methods.handleSubmit(handleDeliveryFormSubmit)}
          data-testid="address-form"
        >
          <div className="row">
            <div className="col-sm-6 col-md-4 col-md-offset-1">
              <AddressInformation
                countryStates={
                  currentAddressModel.PrimaryAddress.ReferenceStates ||
                  currentAddressModel.AustralianStates
                }
                countries={currentAddressModel.Countries}
              />
              <div className="form-section">
                {fields.map((field, index) => (
                  <AddressInformation
                    key={field.id}
                    namePrefix={`secondary[${index}]`}
                    idPrefix={`secondary.${index}.`}
                    countryStates={
                      currentAddressModel.SecondaryAddresses[index]?.ReferenceStates ||
                      currentAddressModel.AustralianStates
                    }
                    countries={currentAddressModel.Countries}
                    header={
                      <>
                        <h3 className="h4">Additional delivery address {index + 1}</h3>
                        <p className="secondary-address-actions">
                          <a
                            href="#"
                            role="button"
                            onClick={(event) => {
                              handleMakePrimary(event, index);
                            }}
                            tabIndex={-1}
                          >
                            Make primary
                          </a>
                          {'| '}
                          <a
                            href="#"
                            role="button"
                            onClick={(event) => {
                              handleRemoveAddress(event, index);
                            }}
                            tabIndex={-1}
                          >
                            Delete
                          </a>
                        </p>
                      </>
                    }
                  />
                ))}
              </div>
              <div className="form-section">
                <p className="large">
                  <a role="button" onClick={handleAddAddress} tabIndex={-1}>
                    Add a new delivery address
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-1">
              {errorMessage && (
                <div className="callout-row">
                  <div className="callout-container callout-error">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}
              {successMessage && (
                <div className="callout-row">
                  <div className="callout-container callout-success">
                    <p>{successMessage}</p>
                  </div>
                </div>
              )}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!isDirty || formHasErrors || isBusy}
              >
                {isBusy ? 'Processing...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export const DeliveryAddressFormSC = (props) => {
  const { sitecoreData } = props;
  return <DeliveryAddressForm currentAddressModel={sitecoreData} />;
};

DeliveryAddressFormSC.propTypes = {
  sitecoreData: PropTypes.object.isRequired,
};

DeliveryAddressForm.propTypes = {
  currentAddressModel: PropTypes.object.isRequired,
};
